/**
 * Shared types for consent form templates.
 */
import type { ComponentType } from 'react'

import type { Clause } from '@/frontend/features/printing/documents/consent/clauses'
import type { DocumentClinic } from '@/frontend/features/printing/documents/types'

/** A medication entry in the pre-op questions section. */
export interface ConsentMedicationEntry {
  key: string
  name: string
  dateTimeGiven: string
}

/** Fields shared by all consent forms — the pre-op questions + signature. */
export interface ConsentFormFields {
  lastEatTime: string
  onMedication: 'yes' | 'no' | ''
  medications: ConsentMedicationEntry[]
  hasAllergies: 'yes' | 'no' | ''
  allergiesDetails: string
  hasConcerningSigns: 'yes' | 'no' | ''
  concerningSignsDetails: string
  feedingConsent: 'yes' | 'no' | ''
  optInBloodTests: boolean
}

/** Create 3 empty medication placeholder rows. */
export function emptyMedicationRows(): ConsentMedicationEntry[] {
  return Array.from({ length: 3 }, () => ({
    key: crypto.randomUUID(),
    name: '',
    dateTimeGiven: '',
  }))
}

export const defaultConsentFormFields: ConsentFormFields = {
  lastEatTime: '',
  onMedication: '',
  medications: emptyMedicationRows(),
  hasAllergies: '',
  hasConcerningSigns: '',
  allergiesDetails: '',
  concerningSignsDetails: '',
  feedingConsent: '',
  optInBloodTests: false,
}

/** Configuration for building a consent form template. */
export interface ConsentTemplateConfig {
  id: string
  label: string
  /** Document title (e.g. "GENERAL ANAESTHETIC & CASTRATE CONSENT"). */
  title: string
  /** Clinic whose details appear in the header. Defaults to Snoots HQ. */
  clinic?: DocumentClinic
  /** Renders the consent paragraph in the interactive preview (HTML). */
  renderConsentPreview: ComponentType<{ patientName: string }>
  /** Renders the consent paragraph in the PDF (react-pdf components). */
  renderConsentPrint: ComponentType<{ patientName: string }>
  /** Procedure-specific clauses shown after the pre-op questions. */
  clauses: Clause[]
}
